import React, { useState, useRef, useEffect } from 'react';
import { CircleUser, SquareUser, X } from 'lucide-react';
import { api } from '../../../../utils/api';
import { API_ENDPOINTS, S3_BASE_URL } from '../../../../config';
import '../../../../styles/resume-editor.css';

interface HeadshotProps {
  onHeadshotChange?: (settings: { is_headshot?: boolean; headshot_url?: string; headshot_format?: 'circle' | 'square' }) => void;
  onHeadshotFormatChange?: (format: 'circle' | 'square') => void;
  onHeadshotUpload?: (headshotUrl: string) => void;
  cvUid?: string;
  initialHeadshot?: boolean;
  initialHeadshotFormat?: 'circle' | 'square';
  initialHeadshotUrl?: string;
}

export const Headshot: React.FC<HeadshotProps> = ({ 
  onHeadshotChange, 
  onHeadshotFormatChange, 
  onHeadshotUpload,
  cvUid, 
  initialHeadshot, 
  initialHeadshotFormat = 'circle',
  initialHeadshotUrl 
}) => {
  const [includeHeadshot, setIncludeHeadshot] = useState(initialHeadshot ?? false);
  const [headshotFormat, setHeadshotFormat] = useState<'circle' | 'square'>(initialHeadshotFormat);
  const [headshotUrl, setHeadshotUrl] = useState<string | undefined>(initialHeadshotUrl);
  const [isUploading, setIsUploading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevHeadshotRef = useRef<boolean>(initialHeadshot ?? false);

  useEffect(() => {
    if (initialHeadshot !== undefined) {
      setIncludeHeadshot(initialHeadshot);
      prevHeadshotRef.current = initialHeadshot;
    }
  }, [initialHeadshot]);

  useEffect(() => {
    if (initialHeadshotFormat) {
      setHeadshotFormat(initialHeadshotFormat);
    }
  }, [initialHeadshotFormat]);

  useEffect(() => {
    if (initialHeadshotUrl !== undefined) {
      setHeadshotUrl(initialHeadshotUrl);
    }
  }, [initialHeadshotUrl]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !cvUid) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setIsUploading(true);
    setIsModalOpen(false);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.postFormData<{ headshot_url: string }>(
        API_ENDPOINTS.cvs.uploadHeadshotImage(cvUid),
        formData
      );

      const newHeadshotUrl = response.headshot_url || (response as any).headshot_url;
      setHeadshotUrl(newHeadshotUrl);
      
      if (onHeadshotUpload) {
        onHeadshotUpload(newHeadshotUrl);
      }
      
      if (onHeadshotChange) {
        onHeadshotChange({ headshot_url: newHeadshotUrl });
      }
    } catch (error: any) {
      console.error('Failed to upload headshot:', error);
      alert('Failed to upload headshot. Please try again.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getHeadshotUrl = () => {
    if (!headshotUrl) return '';
    if (headshotUrl.startsWith('http')) return headshotUrl;
    return `${S3_BASE_URL}${headshotUrl}`;
  };

  const handlePlaceholderClick = () => {
    if (isUploading) return;
    fileInputRef.current?.click();
  };

  const handleRemoveHeadshot = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!cvUid || !headshotUrl || isUploading) return;

    setIsUploading(true);

    try {
      await api.post(API_ENDPOINTS.cvs.removeHeadshot(cvUid));
      
      setHeadshotUrl(undefined);
      
      if (onHeadshotUpload) {
        onHeadshotUpload('');
      }
      
      if (onHeadshotChange) {
        onHeadshotChange({ headshot_url: undefined });
      }
    } catch (error: any) {
      console.error('Failed to remove headshot:', error);
      alert('Failed to remove headshot. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="resume-editor__section-expanded-body">
      <div className="headshot__toggle-section">
        <label className="headshot__toggle-label">
          <span className="headshot__toggle-text">Include Headshot</span>
          <div
            className={`headshot__toggle-switch ${includeHeadshot ? 'headshot__toggle-switch--active' : ''}`}
            onClick={() => {
              const newValue = !includeHeadshot;
              if (newValue !== prevHeadshotRef.current) {
                setIncludeHeadshot(newValue);
                prevHeadshotRef.current = newValue;
                onHeadshotChange?.({ is_headshot: newValue, headshot_format: headshotFormat });
              }
            }}
          >
            <div className="headshot__toggle-switch-thumb" />
          </div>
        </label>
      </div>

      {includeHeadshot && (
        <>
          <div className="headshot__photo-section">
            <label className="headshot__photo-label">Headshot Photo</label>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <div 
              className="headshot__photo-placeholder"
              onClick={handlePlaceholderClick}
              style={{ cursor: isUploading ? 'wait' : 'pointer' }}
            >
              {isUploading ? (
                <div className="headshot__photo-loading">Uploading...</div>
              ) : headshotUrl ? (
                <>
                  <img 
                    src={getHeadshotUrl()} 
                    alt="Headshot" 
                    className="headshot__photo-image"
                    onError={(e) => {
                      console.error('Failed to load headshot image:', headshotUrl);
                      console.error('Full URL:', getHeadshotUrl());
                    }}
                  />
                  <button
                    className="headshot__photo-remove"
                    onClick={handleRemoveHeadshot}
                    type="button"
                    title="Remove headshot"
                  >
                    <X size={10} />
                  </button>
                </>
              ) : (
                <CircleUser size={24} className="headshot__photo-icon" />
              )}
            </div>
          </div>

          <div className="headshot__format-section">
            <div className="headshot__format-label">Format</div>
            <div className="headshot__format-options">
              <label className="headshot__format-option">
                <input
                  type="radio"
                  name="headshotFormat"
                  value="circle"
                  checked={headshotFormat === 'circle'}
                  onChange={(e) => {
                    const newFormat = e.target.value as 'circle' | 'square';
                    setHeadshotFormat(newFormat);
                    onHeadshotFormatChange?.(newFormat);
                  }}
                />
                <CircleUser size={14} />
                <span>Circle</span>
              </label>
              <label className="headshot__format-option">
                <input
                  type="radio"
                  name="headshotFormat"
                  value="square"
                  checked={headshotFormat === 'square'}
                  onChange={(e) => {
                    const newFormat = e.target.value as 'circle' | 'square';
                    setHeadshotFormat(newFormat);
                    onHeadshotFormatChange?.(newFormat);
                  }}
                />
                <SquareUser size={14} />
                <span>Square</span>
              </label>
            </div>
          </div>
        </>
      )}

      <div className="headshot__tip-box">
        <div className="headshot__tip-content">
          <div className="headshot__tip-text">
            <strong>Tip:</strong> Add a headshot only if it's customary in your country. Otherwise, skip it so recruiters can focus on your skills and experience.
          </div>
          <a href="#" className="headshot__tip-link" onClick={(e) => e.preventDefault()}>
            Learn more
          </a>
        </div>
      </div>
    </div>
  );
};

